import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import CrudDialog, { ConfirmDeleteDialog } from "@/components/app/CrudDialog";
import { toast } from "sonner";
import { Search, PlusCircle, Pencil, Trash2 } from "lucide-react";

export default function Catalogue() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    api.get("/catalogue/products", { params: { q: q || undefined, limit: 100 } })
      .then(({ data }) => setRows(data.items || []));
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, q]);
  useEffect(() => {
    Promise.all([api.get("/catalogue/brands"), api.get("/catalogue/categories")]).then(([b, c]) => {
      setBrands(b.data.items || []);
      setCategories(c.data.items || []);
    });
  }, []);

  const archive = async (p) => {
    try { await api.delete(`/catalogue/products/${p.id}`); toast.success("Product deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fields = [
    { name: "name", label: "Product name", required: true, placeholder: "MacBook Pro 14 M4" },
    { name: "sku", label: "SKU", required: true, placeholder: "MBP14-M4-16-512" },
    { name: "brand", label: "Brand", type: "select", required: true, options: brands.map((b) => ({ value: b.name, label: b.name })) },
    { name: "category", label: "Category", type: "select", required: true, options: categories.map((c) => ({ value: c.name, label: c.name })) },
    { name: "config", label: "Configuration", placeholder: "M4 / 16 GB / 512 GB" },
    { name: "list_price", label: "List price", type: "number", placeholder: "1099" },
    { name: "currency", label: "Currency", placeholder: "USD" },
    { name: "warranty_months", label: "Warranty (months)", type: "number", placeholder: "12" },
  ];

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Commercial</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Product Catalogue</h1>
          <p className="text-sm text-slate-500 mt-0.5">Master SKU list across brands and categories.</p>
        </div>
        <Button className="bg-slate-900" onClick={() => setDialog({ open: true, initial: null })} data-testid="prod-add-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add product
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by model, SKU, name…" className="pl-9 h-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="prod-search" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((p) => (
          <div key={p.id} data-testid={`prod-card-${p.id}`} className="bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm transition-shadow flex flex-col">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{p.brand} · {p.category}</div>
                <div className="mt-1 font-heading text-base font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500 font-mono mt-1">{p.sku}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setDialog({ open: true, initial: p })} data-testid={`prod-edit-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => setConfirm(p)} data-testid={`prod-archive-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="text-sm text-slate-700 mt-2 flex-1">{p.config}</div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm text-slate-900 font-medium">{p.currency} {(p.list_price ?? 0).toLocaleString()}</div>
              <div className="text-xs text-slate-500">Warranty {p.warranty_months}m</div>
            </div>
          </div>
        ))}
      </div>

      <CrudDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        title={dialog.initial ? `Edit ${dialog.initial.name}` : "Add product"}
        fields={fields}
        initial={dialog.initial}
        endpoint={dialog.initial ? `/catalogue/products/${dialog.initial.id}` : "/catalogue/products"}
        method={dialog.initial ? "PATCH" : "POST"}
        onSaved={load}
        testIdPrefix="prod"
      />
      <ConfirmDeleteDialog
        open={!!confirm}
        onOpenChange={() => setConfirm(null)}
        title={`Delete ${confirm?.name}?`}
        description="Product will be marked inactive. Existing assets are unaffected."
        onConfirm={() => archive(confirm)}
        testId="prod-confirm"
      />
    </div>
  );
}
